<template>
  <div
    class="h-full text-center g-safe-area px-4 flex flex-col bg-ld-background"
  >
    <div
      class="flex-1 g-body-width overflow-hidden flex flex-col items-center mt-8"
    >
      <div
        class="text-25px mb-4 flex items-center justify-center color-[var(--yellow-500)] font-bold space-x-4"
      >
        <Icon
          name="wheat-left"
          class="w-16px"
        />
        <span>
          {{
            hasRankImprovement
              ? _t('rank.rank_improvement')
              : _t('rank.my_rank')
          }}
        </span>
        <Icon
          name="wheat-right"
          class="w-16px"
        />
      </div>

      <RankIcon
        :rank="groupRank"
        class="w-90px h-90px mt-8 mb-4"
      />

      <div class="text-21px font-semibold">
        <!-- 有排名提升时 -->
        <template v-if="hasRankImprovement">
          <template v-if="afterRankPosition > 3">
            {{ _t('rank.msg14') }}
            <span class="text-ld-brand-500">
              {{
                _t('rank.msg15', {
                  count: beforeRankPosition - afterRankPosition,
                })
              }}
            </span>
          </template>
          <template v-else>
            {{ _t('rank.msg16') }}
            <span class="text-ld-brand-500">
              {{ _t('rank.msg17', { rank: afterRankPosition }) }}
            </span>
          </template>
        </template>

        <!-- 没有排名提升时 -->
        <template v-else>
          <template v-if="expNeededForNextRank > 0">
            {{ _t('rank.msg_encourage_part1') }}
            <span class="text-ld-brand-500">
              {{ expNeededForNextRank }} {{ _t('common.currency_exp') }}
            </span>
            {{ _t('rank.msg_encourage_part2') }}
          </template>
          <template v-else>
            {{ _t('rank.msg_already_first') }}
          </template>
        </template>
      </div>

      <div class="flex-1 w-full mt-8 overflow-y-hidden">
        <TransitionGroup
          ref="groupListRef"
          name="group"
          tag="ul"
          class="group w-full overflow-y-auto max-h-full p-0 m-0"
        >
          <div
            v-for="user in groupUsers"
            :key="user.userId"
            :class="[
              'relative',
              {
                'self-user': user.isSelf,
              },
            ]"
          >
            <GroupUserItem
              :key="user.userId"
              :user="user"
            ></GroupUserItem>

            <div
              v-if="user.isSelf && isAnimationEnd"
              class="lottie-sparkle w-full"
            >
              <LottieBox
                name="sparkle-brand-0"
                class="sparkle-left"
                autoplay
                :loop="false"
              />

              <LottieBox
                name="sparkle-brand-1"
                class="sparkle-right"
                autoplay
                :loop="false"
              />
            </div>
          </div>
        </TransitionGroup>
      </div>
    </div>

    <div class="h-53px mb-4 mt-4">
      <Button
        v-if="isAnimationEnd"
        class="w-full g-footer-width g-fade-in"
        label="继续"
        @click="onContinue"
      ></Button>
    </div>
  </div>
</template>
<script lang="ts" setup>
import type { RankHomeInGroup } from '@/types/rank'
import { computed, nextTick, ref } from 'vue'
import { parseRank } from '../Rank/util'
import RankIcon from '@/components/RankIcon.vue'
import GroupUserItem, {
  type DisplayRankUser,
} from '@/components/RankGroupUserItem.vue'
import LottieBox from '@/components/LottieBox.vue'
import { vibrate, VibrateType } from '@/utils/misc'

const props = defineProps<{
  beforeRank?: RankHomeInGroup
  afterRank: RankHomeInGroup
}>()

const emit = defineEmits<{
  done: []
}>()

const isAnimationEnd = ref(false)
const groupUsers = ref<DisplayRankUser[]>([])
const groupListRef = ref<any>()

const groupRank = computed(() => parseRank(props.afterRank.data.groupRank))

const beforeRankPosition = computed(() => {
  if (props.beforeRank == null) {
    return props.afterRank.data.users.length
  }

  return props.beforeRank.data.position
})

const afterRankPosition = computed(() => props.afterRank.data.position)

const beforeRankIndex = computed(() => beforeRankPosition.value - 1)
const afterRankIndex = computed(() => afterRankPosition.value - 1)

// 判断是否有排名提升
const hasRankImprovement = computed(() => {
  return afterRankPosition.value < beforeRankPosition.value
})

// 获取当前用户的经验值变化
const currentUserExp = computed(() => {
  // 从学习后的排名数据中获取当前用户
  const userAfterLearn = props.afterRank.data.users[afterRankIndex.value]
  // 从学习前的排名数据中找到同一用户
  const userBeforeLearn = props.beforeRank?.data.users.find(
    u => u.userId === userAfterLearn.userId
  )

  return {
    userId: userAfterLearn.userId,
    beforeExp: userBeforeLearn?.exp || 0, // 学习前的经验值
    afterExp: userAfterLearn.exp, // 学习后的经验值
    expGain: userAfterLearn.exp - (userBeforeLearn?.exp || 0),
  }
})

// 计算到下一名次所需经验
const expNeededForNextRank = computed(() => {
  const currentPosition = afterRankPosition.value
  if (currentPosition === 1) return 0

  const previousUser = props.afterRank.data.users[currentPosition - 2]
  const expDiff = previousUser.exp - currentUserExp.value.afterExp
  return Math.max(0, expDiff + 1)
})

onInit(() => {
  groupUsers.value = getGroupUsers()

  setTimeout(() => {
    setUserPosition()
    nextTick(changeUserPosition)
  }, 50)
})

function changeUserPosition() {
  if (hasRankImprovement.value) {
    // 有排名提升时，播放原有的排名上升动画
    const meIndex = groupUsers.value.findIndex(item => item.isSelf)
    const me = groupUsers.value[meIndex]
    groupUsers.value.splice(meIndex, 1)
    groupUsers.value.splice(afterRankIndex.value, 0, me)
    groupUsers.value.forEach((item, i) => {
      item.index = i
    })
  } else {
    // 没有排名提升时，播放经验增加动画
    playExpGainAnimation()
  }

  // 动画结束后显示继续按钮
  // TODO(buding): TransitionGroup 事件都没有效果
  // 优化一下动画结束时的判断
  setTimeout(() => {
    isAnimationEnd.value = true
    vibrate(VibrateType.Success)
  }, 1000)
}

// 先把列表视口滚动到当前用户的位置
function setUserPosition() {
  if (groupListRef.value) {
    const currentUser = groupListRef.value.$el.childNodes[afterRankIndex.value]
    currentUser.scrollIntoView({
      block: 'center',
      inline: 'center',
      behavior: 'auto',
    })
  }
}

function getGroupUsers(): DisplayRankUser[] {
  const users = [...props.afterRank.data.users]

  const me = users[afterRankIndex.value]

  // 先把自己从列表中移除
  users.splice(afterRankIndex.value, 1)

  if (props.beforeRank == null) {
    users.push(me)
  } else {
    users.splice(beforeRankIndex.value, 0, me)
  }

  return users.map((user, i) => {
    const isSelf = user.userId === me.userId

    // 如果没有排名提升且是当前用户，先显示学习前的经验值
    if (!hasRankImprovement.value && isSelf) {
      return {
        ...user,
        index: i,
        isSelf,
        exp: currentUserExp.value.beforeExp, // 先显示学习前的经验值
      }
    }

    return {
      ...user,
      index: i,
      isSelf,
    }
  })
}

function playExpGainAnimation() {
  // 1. 找到当前用户在排行榜中的对象
  const currentUserIndex = groupUsers.value.findIndex(user => user.isSelf)
  if (currentUserIndex === -1 || currentUserExp.value.expGain <= 0) {
    return
  }

  // 2. 播放经验增加动画
  animateExpIncrease(
    currentUserExp.value.beforeExp,
    currentUserExp.value.afterExp,
    currentUserIndex
  )

  // 3. 经验动画结束后播放强调动画
  setTimeout(() => {
    highlightCurrentUser()
  }, 800)
}

function animateExpIncrease(fromExp: number, toExp: number, userIndex: number) {
  const duration = 800
  const startTime = Date.now()

  // 初始化动画经验值
  groupUsers.value[userIndex].animatedExp = fromExp

  function updateExp() {
    const elapsed = Date.now() - startTime
    const progress = Math.min(elapsed / duration, 1)

    const currentExp = Math.floor(fromExp + (toExp - fromExp) * progress)
    groupUsers.value[userIndex].animatedExp = currentExp

    if (progress < 1) {
      requestAnimationFrame(updateExp)
    } else {
      // 动画结束后设置最终经验值并清除 animatedExp
      groupUsers.value[userIndex].exp = toExp
      groupUsers.value[userIndex].animatedExp = undefined
    }
  }

  requestAnimationFrame(updateExp)
}

function highlightCurrentUser() {
  const currentUserElement = document.querySelector('.self-user')
  if (currentUserElement) {
    currentUserElement.classList.add('rank-highlight')
    setTimeout(() => {
      currentUserElement.classList.remove('rank-highlight')
    }, 1000)
  }
}

function onContinue() {
  emit('done')
}
</script>
<style scoped>
.group {
  background-color: white;
  border: 2px solid var(--gray-300);
  border-radius: 12px;
}

.group-move, /* apply transition to moving elements */
.group-enter-active,
.group-leave-active {
  transition: all 1s ease;
}

/* 这里需要设置当前用户的 position 和 z-index，否则会从其他的元素中穿过 */
.group-move.self-user {
  position: relative;
  z-index: 1;
}

.lottie-sparkle {
  position: absolute;
  bottom: -6px;
  left: 0;
  right: 0;
  top: -6px;
  transform: scale(1, -1);
}

.sparkle-left {
  left: 0;
  top: 8px;
  width: 40px;
  position: absolute;
}
.sparkle-right {
  bottom: 0;
  right: 0;
  width: 30px;
  position: absolute;
}

/* 强调当前排名的样式 */
.rank-highlight {
  background-color: rgba(139, 92, 246, 0.2) !important; /* 浅紫色背景 */
  transition: background-color 0.3s ease;
}
</style>
